/**
 * LOGOI LAB - Layer 1: Friston Free Energy Engine
 * F(x) = D_KL(q || p_Logos) — Divergence from Logos-optimal state
 * 
 * @author Dr. José Manuel Cadena Ortiz de Montellano
 * @framework Cadena Strategic Systems
 */

import type { ConsciousnessState, FreeEnergyResult } from '../types';
import { clamp } from '../utils/math';

const IDEALS: Record<string, number> = {
  sleep: 0.85, nutrition: 0.80, exercise: 0.75, energy: 0.80,
  peace: 0.75, gratitude: 0.80, love: 0.75, joy: 0.70,
  clarity: 0.75, focus: 0.75, creativity: 0.65, wisdom: 0.70,
  faith: 0.70, meditation: 0.65, service: 0.60, presence: 0.70,
  family: 0.75, friendship: 0.65, community: 0.55, compassion: 0.70,
  meaning: 0.75, mission: 0.70, contribution: 0.65, legacy: 0.55,
  nourishment: 0.80, taste_presence: 0.65, food_harmony: 0.70, gut_resonance: 0.70,
};

const WEIGHTS: Record<string, number> = {
  sleep: 0.06, nutrition: 0.04, exercise: 0.05, energy: 0.05,
  peace: 0.05, gratitude: 0.04, love: 0.04, joy: 0.04,
  clarity: 0.04, focus: 0.04, creativity: 0.03, wisdom: 0.04,
  faith: 0.05, meditation: 0.03, service: 0.03, presence: 0.04,
  family: 0.03, friendship: 0.03, community: 0.02, compassion: 0.03,
  meaning: 0.03, mission: 0.03, contribution: 0.02, legacy: 0.02,
  nourishment: 0.04, taste_presence: 0.02, food_harmony: 0.02, gut_resonance: 0.04,
};

/**
 * Compute Free Energy F(x) - Friston Layer
 * 
 * Measures divergence between current state and Logos-optimal state.
 * Higher Λ reduces effective F (Logos reduces surprise).
 * 
 * @param s - Current consciousness state
 * @param Lambda - Current Logos alignment value
 * @returns FreeEnergyResult
 */
export function computeFreeEnergy(s: ConsciousnessState, Lambda: number): FreeEnergyResult {
  let F_raw = 0;
  const breakdown: any[] = [];
  
  for (const k of Object.keys(IDEALS)) {
    const actual = (s as any)[k] ?? 0.5;
    const ideal = IDEALS[k];
    const weight = WEIGHTS[k];
    const diff = actual - ideal;
    const contrib = weight * (diff * diff) / (2 * 0.04);
    
    F_raw += contrib;
    breakdown.push({ 
      key: k, 
      ideal, 
      actual, 
      weight, 
      contribution: contrib 
    });
  }
  
  // Λ modulates F: higher Λ → lower effective F (Logos reduces surprise)
  const lambdaEffect = Lambda * 0.3;
  const F_modulated = F_raw * (1 - lambdaEffect);
  
  return { 
    value: clamp(F_modulated, 0, 1.5), 
    raw: F_raw, 
    modulated: F_modulated, 
    breakdown, 
    lambdaEffect 
  };
}
